/**
 * Horizontal part-of-whole bar (e.g. sellable / displayed stock, delivered /
 * ordered). No dependency; colours come from the theme tokens so light and dark
 * mode both work. Exposed as one role="img" with a "x / y (%z)" label.
 */

export type RatioBarTone = 'success' | 'warn' | 'danger' | 'brand' | 'neutral';

const TONE_FILL: Record<RatioBarTone, string> = {
  success: 'var(--success-500)',
  warn: 'var(--warn-500)',
  danger: 'var(--danger-500)',
  brand: 'var(--brand-500)',
  neutral: 'var(--gray-400)',
};

export interface RatioBarProps {
  readonly value: number;
  readonly total: number;
  readonly tone?: RatioBarTone;
  /** Accessible name prefix, e.g. 'Teslim oranı'. */
  readonly label?: string;
  readonly height?: number;
  readonly testId?: string;
}

/** Whole-number percentage of value/total, clamped to 0..100; null when total <= 0. */
export function ratioPercent(value: number, total: number): number | null {
  if (!(total > 0) || !Number.isFinite(value)) return null;
  return Math.max(0, Math.min(100, Math.round((value / total) * 100)));
}

export function RatioBar({ value, total, tone = 'brand', label, height = 6, testId }: RatioBarProps): JSX.Element {
  const pct = ratioPercent(value, total);
  const text =
    pct === null
      ? `${label ? `${label}: ` : ''}veri yok`
      : `${label ? `${label}: ` : ''}${value.toLocaleString('tr-TR')} / ${total.toLocaleString('tr-TR')} (%${pct})`;
  return (
    <div
      role="img"
      aria-label={text}
      title={text}
      data-testid={testId}
      data-percent={pct ?? undefined}
      style={{
        position: 'relative',
        width: '100%',
        height,
        borderRadius: 999,
        background: 'var(--bg-sunken)',
        boxShadow: 'inset 0 0 0 1px var(--border-subtle)',
        overflow: 'hidden',
      }}
    >
      <div
        style={{
          width: `${pct ?? 0}%`,
          height: '100%',
          borderRadius: 999,
          background: TONE_FILL[tone],
          transition: 'width 240ms ease',
        }}
      />
    </div>
  );
}
